/* eslint-disable camelcase */
import get from 'lodash/get.js';
import has from 'lodash/has.js';
import isEmpty from 'lodash/isEmpty.js';
import merge from 'lodash/merge.js';
import toArray from 'lodash/toArray.js';
import uuidV4 from 'uuid/v4';

import Section from '@/models/Section.js';
import SectionTemplate from '@/models/SectionTemplate.js';
import axios from '@/plugins/axios.js';
import moment from '@/plugins/vue-moment.js';
import BaseSection from '@/store/modules/base/BaseSection.js';
import Collection from '@/store/quill/Collection.js';
import ActivityHelper from '@/store/utilities/ActivityHelpers.js';
import { courseDatePayload, persistAppliedCourseSettings } from '@/utilities/courseSettingsPersist.js';

const settings_type = name => (name.includes('emp') ? 'section_settings_template' : 'section_settings');

function normalizeAssignWeekOrderOptions(options) {
    if (typeof options === 'boolean') {
        return {
            checkCourseSettings: options,
            skipCourseSettingsPersist: false,
        };
    }

    return {
        checkCourseSettings: options?.checkCourseSettings !== false,
        skipCourseSettingsPersist: options?.skipCourseSettingsPersist === true,
    };
}

const helper = new ActivityHelper();

const DB_FORMAT = 'YYYY-MM-DD HH:mm:ss';

export default function ({ name }) {
    return merge(Collection(name, BaseSection), {
        state: {},

        getters: {
            getByKey(state, getters, rootState, rootGetters) {
                return ({ keyName, key, field }) => {
                    const uuid = Object.keys(state).find(
                        entry => getters.has(entry) && getters.get(entry)[keyName] === key
                    );

                    if (!field) {
                        return uuid ? rootGetters[`${name}/get`](uuid) : null;
                    }

                    return uuid ? rootGetters[`${name}/${uuid}/get`](field) : null;
                };
            },

            nestedItem:
                state =>
                ({ id, item, field }) => {
                    const entry = state[id];
                    if (!entry) return null;
                    const nested = entry[item];
                    if (!nested) return null;
                    return nested[field] ?? null;
                },

            getWeeks:
                (state, getters) =>
                ({ settings }) => {
                    const sections = toArray(getters.all());

                    sections.sort((a, b) => (a.order > b.order ? 1 : -1));

                    return sections.filter(section => ['week', 'break'].includes(section[settings].type));
                },

            getEndDates:
                (state, getters, rootState, rootGetters) =>
                ({ start_date }) => {
                    const timezone = rootGetters['InstitutionSettings/get']('timezone');

                    if (!start_date) return null;

                    const weeks = getters.getWeeks({
                        settings: settings_type(name),
                    });

                    if (weeks.length >= 1) {
                        const max = weeks.length * 7 - 1;
                        const min = (weeks.length - 1) * 7;

                        // Fallback to UTC if timezone not available
                        const startMoment = timezone ? moment.utc(start_date).tz(timezone) : moment.utc(start_date);

                        return {
                            min: startMoment.clone().add(min, 'days').toDate(),
                            max: startMoment.clone().add(max, 'days').toDate(),
                        };
                    }

                    return null;
                },

            getWeekStatus:
                (state, getters, rootState, rootGetters) =>
                ({ uuid, template }) => {
                    const item = getters.get(uuid);

                    if (!item) return;

                    if (item.sectionType === 'week') {
                        return helper.getSectionDueDates(item, rootGetters, template);
                    }

                    if (template) {
                        if (item.parent_id) {
                            const parent = rootGetters['SectionTemplates/getByKey']({
                                key: Number(item.parent_id),
                                keyName: 'id',
                                field: null,
                            });

                            if (!parent) throw new Error('An error occurred, Edverum support has been notified');

                            return helper.getSectionDueDates(parent, rootGetters, template);
                        }
                    }

                    if (!template) {
                        if (item.parent_id) {
                            const parent = rootGetters['Sections/getByKey']({
                                key: Number(item.parent_id),
                                keyName: 'id',
                                field: null,
                            });

                            if (!parent) throw new Error('An error occurred, Edverum support has been notified');

                            return helper.getSectionDueDates(parent, rootGetters, template);
                        }
                    }

                    return { type: 'root-level', week: null };
                },
        },

        actions: {
            addNestedItemContent(context, item) {
                context.commit('SETTINGS', item);
            },

            async assignWeekOrder(context, options = true) {
                const { checkCourseSettings, skipCourseSettingsPersist } = normalizeAssignWeekOrderOptions(options);
                const template = name.toLowerCase().includes('temp');
                const settings = settings_type(name);

                const weeks = context.getters.getWeeks({ settings });

                if (isEmpty(weeks)) return;

                let instructionalWeekNumber = 0;

                const orderedSections = await Promise.all(
                    weeks.map((week, index) => {
                        const { type } = week[settings];

                        const weekNumber = index + 1;

                        week[settings].week_number = weekNumber;

                        if (type === 'week') {
                            // Break weeks occupy a slot in the timeline but are not instructional
                            // weeks, so the displayed number counts only week sections. This keeps
                            // numbering before and after a break consistent with the course outline.
                            instructionalWeekNumber += 1;
                            week[settings].display_week_number = instructionalWeekNumber;
                        }

                        context.commit(`${week.uuid}/EDIT_FIELD`, {
                            field: 'updateKey',
                            value: uuidV4(),
                        });

                        return week;
                    })
                );

                if (template) {
                    await context.commit('ASSIGN_ORDER', {
                        sections: orderedSections,
                        settings,
                    });
                }

                if (!template && checkCourseSettings) {
                    await context.dispatch('assignWeekStartAndEnd', {
                        sections: orderedSections,
                        skipCourseSettingsPersist,
                    });
                }
            },

            async assignWeekStartAndEnd(context, { sections, skipCourseSettingsPersist = false }) {
                const course_settings = context.rootGetters['CourseTemplateForm/courseItem']('course_settings');
                const timezone = context.rootGetters['InstitutionSettings/get']('timezone');

                // Fallback to UTC if timezone not available
                const course_start = timezone
                    ? moment.utc(course_settings.start_date).tz(timezone)
                    : moment.utc(course_settings.start_date);

                sections[0].section_settings.week_start = course_start.clone();
                sections[0].section_settings.week_end = course_start.clone().add(1, 'week').subtract(1, 'second');

                sections.reduce((initial, section) => {
                    // This is forcing that the week sections have the proper start and end dates.
                    // It starts from the beginning with the first week and uses the end date of the first week
                    // as its catalyst. The section param at the beginning of the reduce method is the second
                    // index in the sections array. Thus the section param uses the end date of the initial param
                    // as its reference point and builds off of that.

                    // Iteration: [1st index, 2nd index][2nd index, 3rd index]
                    section.section_settings.week_start = initial.section_settings.week_end.clone().add(1, 'seconds');
                    section.section_settings.week_end = initial.section_settings.week_end.clone().add(1, 'week');

                    return section;
                });

                sections.forEach(section => {
                    section.section_settings.week_start = section.section_settings.week_start.utc().format(DB_FORMAT);
                    section.section_settings.week_end = section.section_settings.week_end.utc().format(DB_FORMAT);
                });

                await context.dispatch('checkIfLastWeekSectionPassesCourseEndDate', {
                    course_settings,
                    section: sections[sections.length - 1].section_settings,
                    skipCourseSettingsPersist,
                });

                await context.commit('ASSIGN_ORDER', {
                    sections,
                    settings: 'section_settings',
                });
            },

            async checkIfLastWeekSectionPassesCourseEndDate(
                context,
                { course_settings, section, skipCourseSettingsPersist = false }
            ) {
                const timezone = context.rootGetters['InstitutionSettings/get']('timezone');
                const authUser = context.rootGetters['Auth/user'];
                const course = context.rootGetters['CourseTemplateForm/course'];
                const canUpdateCourseSettings =
                    authUser &&
                    course &&
                    typeof authUser.hasPermissionViaCourseTo === 'function' &&
                    authUser.hasPermissionViaCourseTo(course, 'edit course settings');

                // Fallback to UTC if timezone not available
                const last_section_end_date = timezone
                    ? moment.utc(section.week_end).tz(timezone)
                    : moment.utc(section.week_end);
                const last_section_start_date = timezone
                    ? moment.utc(section.week_start).tz(timezone)
                    : moment.utc(section.week_start);

                const course_end = timezone
                    ? moment.utc(course_settings.end_date).tz(timezone)
                    : moment.utc(course_settings.end_date);

                const persist_settings = async () => {
                    if (!canUpdateCourseSettings) {
                        return;
                    }

                    const currentSettings = {
                        ...context.rootGetters['CourseTemplateForm/courseItem']('course_settings'),
                    };

                    const course_settings_end_moment_object = moment.utc(currentSettings.end_date);
                    const course_settings_end_day_of_week = course_settings_end_moment_object.day();
                    const new_course_settings_day = course_settings_end_day_of_week - 1;

                    currentSettings.end_date = moment
                        .utc(course_settings_end_day_of_week <= 1 ? section.week_end : section.week_start)
                        .add(new_course_settings_day, 'days')
                        .hour(course_settings_end_moment_object.hours())
                        .minute(course_settings_end_moment_object.minutes())
                        .second(course_settings_end_moment_object.seconds())
                        .format(DB_FORMAT);

                    try {
                        const savedSettings = await axios
                            .put(`/api/course-settings/${currentSettings.id}`, courseDatePayload(currentSettings))
                            .then(resp => resp?.data?.data);

                        persistAppliedCourseSettings(context.commit, savedSettings, { formCommitRoot: true });
                    } catch (error) {
                        // Skip date auto-correction when user cannot update course settings.
                        if (get(error, 'response.status') !== 403) {
                            throw error;
                        }
                    }
                };

                if (!course_end.isBetween(last_section_start_date, last_section_end_date)) {
                    if (skipCourseSettingsPersist) {
                        return;
                    }

                    return persist_settings();
                }
            },

            async hydrateSection(context, { payload }) {
                const timezone = context.rootGetters['InstitutionSettings/get']('timezone');
                const settings = settings_type(name);

                payload.displayStatus = { editing: false };

                if (payload.type.includes('sec')) {
                    if (!has(payload, 'items')) {
                        payload.items = [];
                    }
                }

                if (has(payload, settings)) {
                    payload.sectionType = payload[settings].type;

                    // Fallback to UTC if timezone not available
                    ['visible_at', 'hidden_at'].forEach(setting => {
                        if (payload[settings][setting]) {
                            payload[settings][setting] = timezone
                                ? moment.utc(payload[settings][setting]).tz(timezone).toDate()
                                : moment.utc(payload[settings][setting]).toDate();
                        }
                    });

                    if (payload[settings].time_visible) {
                        const dateTime = `${moment().format('YYYY-MM-DD')} ${payload[settings].time_visible}`;

                        payload[settings].time_visible = moment(dateTime).toDate();
                    }
                }

                // Map parent only for non-week/break sections. Weeks/Breaks must be root-level.
                if (payload.parent_id) {
                    if (!['week', 'break'].includes(payload.sectionType)) {
                        payload.parent = context.getters.getByKey({
                            keyName: 'id',
                            key: Number(payload.parent_id),
                            field: 'uuid',
                        });
                    } else if (payload.parent) {
                        delete payload.parent;
                    }
                }

                if (has(payload, 'prerequisite_id')) {
                    // fallback to prevent prerequisite errors
                    if (payload.prerequisite_id !== get(payload, 'prerequisite.course_itemable_id')) {
                        payload.prerequisite_id = get(payload, 'prerequisite.course_itemable_id');
                    }

                    if (!get(payload, 'prerequisite_type')) {
                        payload.prerequisite_type = get(payload, 'prerequisite.type');
                    }
                }

                await context.dispatch('add', payload);
            },

            async persist(context, { sections }) {
                const payload = [];

                await context.dispatch('assignWeekOrder');

                sections.forEach(section => payload.push(context.getters.get(section.uuid)));

                await context.dispatch('AutoSave/weeks', { sections: payload }, { root: true });

                payload.forEach(sectionItem => {
                    if (sectionItem.type === 'section_template') {
                        SectionTemplate.insert({ data: sectionItem });
                    } else {
                        Section.insert({ data: sectionItem });
                    }
                });

                return payload;
            },

            updateItem({ commit, dispatch, getters }, item) {
                if (item.field === 'prerequisite_type' && !item.value) {
                    const target = getters[`${item.uuid}/get`]('prerequisite_uuid');

                    dispatch('removeInversePrerequisites', {
                        target,
                        initiator: item,
                    });

                    dispatch(`${item.uuid}/removePrerequisite`);

                    return;
                }

                if (item.field === 'prerequisite_uuid') {
                    dispatch('setInversePrerequisites', {
                        initiator: item.uuid,
                        recipient: item.value,
                    });
                }

                commit('EDIT', item);
            },

            setInversePrerequisites({ dispatch, rootState }, { initiator, recipient }) {
                const template = name.includes('Temp');
                const activityNamespace = template ? 'ActivityTemplates' : 'Activities';
                const isSection = has(rootState[name], recipient);

                if (isSection) {
                    return dispatch(`${recipient}/setInversePrerequisites`, initiator);
                }

                return dispatch(`${activityNamespace}/${recipient}/setInversePrerequisites`, initiator, { root: true });
            },

            removeInversePrerequisites({ dispatch, rootState }, { target, initiator }) {
                const template = name.includes('Temp');
                const activityNamespace = template ? 'ActivityTemplates' : 'Activities';
                const isSection = has(rootState[name], target);
                const targetNamespace = isSection ? name : activityNamespace;

                if (isSection) {
                    dispatch(`${target}/removeInversePrerequisites`, initiator.uuid);
                } else {
                    dispatch(`${targetNamespace}/${target}/removeInversePrerequisites`, initiator.uuid, { root: true });
                }
            },
        },

        mutations: {
            EDIT(state, item) {
                if (!state[item.uuid]) return;
                state[item.uuid][item.field] = item.value;
            },

            BATCH_EDIT(state, changes) {
                for (const change of changes) {
                    if (state[change.uuid]) {
                        state[change.uuid][change.field] = change.value;
                    }
                }
            },

            SETTINGS(state, item) {
                if (item.field === 'remove_visible_at') return;

                if (!state[item.uuid]) return;

                if (!state[item.uuid][item.type]) {
                    state[item.uuid][item.type] = {};
                }

                state[item.uuid][item.type][item.field] = item.value;
            },

            ASSIGN_ORDER(state, { sections, settings }) {
                sections.forEach(section => {
                    state[section.uuid][settings] = section[settings];
                    state[section.uuid].updateKey = uuidV4();
                });
            },

            AUTO_SAVE() {},
        },
    });
}
